import { useMemo, useRef } from 'react';

import type { ScheduleSearchSummary } from '@src/models/interfaces/schedule.interface';
import { ScheduleType } from '@src/models/interfaces/schedule.interface';
import { useElasticDivisionIds } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import useGetDivisionsAllowed from '@src/packages/utils/hooks/useGetDivisionsAllowed';
import ScheduleService from '@src/services/ScheduleService';

import { resolveSearchDivisionIds } from '../utils/searchDivisionIds';
import useDebouncedRemoteSearch from './useDebouncedRemoteSearch';

const SEARCH_PAGE_SIZE = 8;

interface UseSidebarScheduleSearchParams {
  enabled: boolean;
  orgId?: string;
  searchTerm: string;
  types: ScheduleType[];
}

const useSidebarScheduleSearch = ({
  enabled,
  orgId,
  searchTerm,
  types,
}: UseSidebarScheduleSearchParams) => {
  const scheduleServiceRef = useRef<ScheduleService | null>(null);
  if (scheduleServiceRef.current === null) {
    scheduleServiceRef.current = new ScheduleService();
  }

  const { allowedDivsIds, allowedSelectedDivs } = useGetDivisionsAllowed(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.UPDATE,
  );
  const elasticDivIds = useElasticDivisionIds();
  const divisionIds = useMemo(
    () => resolveSearchDivisionIds(elasticDivIds, allowedDivsIds, allowedSelectedDivs),
    [elasticDivIds, allowedDivsIds, allowedSelectedDivs],
  );
  const typesKey = types.join(',');

  const { results, loading, error } = useDebouncedRemoteSearch<ScheduleSearchSummary>({
    enabled: enabled && Boolean(orgId) && divisionIds !== null && types.length > 0,
    searchTerm,
    scopeKey: `${orgId}|${divisionIds?.join(',') ?? ''}|${typesKey}`,
    search: async normalizedTerm => {
      if (!orgId || !divisionIds || !scheduleServiceRef.current) return [];
      return scheduleServiceRef.current.searchSummaries({
        orgId,
        divsIds: divisionIds,
        searchTerm: normalizedTerm,
        types,
        pageSize: SEARCH_PAGE_SIZE,
      });
    },
  });

  return { schedules: results, loading, error };
};

export default useSidebarScheduleSearch;
